<template>
  <ShaftModelLoader v-if="loadingSetElements" />
  <div
    v-if="!loadingSetElements"
    ref="sceneContainer"
    class="scene-container location-preview"
  />
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';

// Utils
import {
  ShaftElement,
  IssuePoint,
  handlePointPlacement,
  IssuePointCrosshair
} from '@/functions/shaft3dModel';
import { useShaft3dModel } from '@/use/useShaft3dModel';
import useAjax from '@/functions/use_ajax';
import ShaftModelLoader from '@/components/shaftsV2/shaft_model_loader.vue';

const props = defineProps({
  organisationId: { type: String, required: true },
  shaftId: { type: String, required: true },
  currentPoint: { type: Object, required: false, default: null },
});

const {
  initTHREE,
  SCENE,
  clearScene,
  focusOnElement,
  getAllElements,
  disableCameraControls,
  setElementMaterialType,
  applyConveyanceStyle,
  displaySidewalls,
} = useShaft3dModel();

const ajax = useAjax();
const shaft = ref(null);
const currentSetElements = ref(null);
const sceneContainer = ref();
const loadingSetElements = ref(false);

const ENDPOINTS = computed(() => {
  return {
    shaft: `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}`,
    setElements: `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}/sets/${props.currentPoint.set}`,
  };
});

onMounted(async () => {
  await loadShaft();
  load3dModel();
});

async function loadShaft() {
  if (props.shaftId) {
    let { ok, data } = await ajax(ENDPOINTS.value.shaft);

    if (ok) {
      shaft.value = data;
    }
  }
}

async function load3dModel() {
  await clearScene(SCENE);
  await loadSetElements();
  await initTHREE(sceneContainer.value, shaft.value.dimensions);
  disableCameraControls();
  renderElements();
}

async function loadSetElements() {
  loadingSetElements.value = true;

  let { ok, data } = await ajax(ENDPOINTS.value.setElements);

  if (ok) {
    currentSetElements.value = data;
  }

  loadingSetElements.value = false;
}

function renderElements() {
  currentSetElements.value.shaft_element_set.forEach((set) => {
    SCENE.add(new ShaftElement(set.shaft_element, shaft.value));
  });

  let elementToFocusOn = getAllElements(SCENE).find((element) => {
    return element.name === props.currentPoint.element.name;
  });

  if (elementToFocusOn) {
    displaySidewalls(elementToFocusOn.userData.type === 'sidewalls');
    getAllElements(SCENE).forEach((element) => {
      setElementMaterialType(element, 'ghosted');
    });

    setElementMaterialType(elementToFocusOn);
    applyConveyanceStyle(SCENE);

    let point = new IssuePoint();

    handlePointPlacement(
      elementToFocusOn,
      point,
      shaft.value.dimensions,
      props.currentPoint.element.position
    );

    focusOnElement(elementToFocusOn);
    new IssuePointCrosshair(point, shaft.value);
  }
}
</script>
